import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { useGpsUserPermissions } from '@/hooks/useGpsUserPermissions';
import { Loader2 } from 'lucide-react';

interface GpsUserPermissionsModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  companyId: string;
}

export function GpsUserPermissionsModal({
  open,
  onClose,
  userId,
  userName,
  companyId,
}: GpsUserPermissionsModalProps) {
  const { myPermissions, isLoading, updatePermissions } = useGpsUserPermissions(companyId);
  
  const [permissions, setPermissions] = useState({
    can_view_live: true,
    can_view_history: true,
    history_days_limit: 30,
    can_send_commands: false,
    can_edit_devices: false,
    can_view_reports: true,
    can_share_tracking: false,
  });

  useEffect(() => {
    if (myPermissions && open) {
      setPermissions({
        can_view_live: myPermissions.can_view_live,
        can_view_history: myPermissions.can_view_history,
        history_days_limit: myPermissions.history_days_limit,
        can_send_commands: myPermissions.can_send_commands,
        can_edit_devices: myPermissions.can_edit_devices,
        can_view_reports: myPermissions.can_view_reports,
        can_share_tracking: myPermissions.can_share_tracking,
      });
    }
  }, [myPermissions, open]);

  const handleSave = async () => {
    await updatePermissions.mutateAsync({
      user_id: userId,
      ...permissions,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Permissões GPS - {userName}</DialogTitle>
          <DialogDescription>
            Configure as permissões específicas do sistema TrackingONE para este usuário
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Visualizar Mapa ao Vivo */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Visualizar Mapa ao Vivo</Label>
                <p className="text-sm text-muted-foreground">
                  Permite ver a localização em tempo real dos veículos
                </p>
              </div>
              <Switch
                checked={permissions.can_view_live}
                onCheckedChange={(checked) =>
                  setPermissions({ ...permissions, can_view_live: checked })
                }
              />
            </div>

            {/* Ver Histórico de Viagens */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Ver Histórico de Viagens</Label>
                  <p className="text-sm text-muted-foreground">
                    Permite consultar viagens anteriores
                  </p>
                </div>
                <Switch
                  checked={permissions.can_view_history}
                  onCheckedChange={(checked) =>
                    setPermissions({ ...permissions, can_view_history: checked })
                  }
                />
              </div>
              {permissions.can_view_history && (
                <div className="ml-4 space-y-2">
                  <Label htmlFor="historyDays">Limite de dias de histórico</Label>
                  <Input
                    id="historyDays"
                    type="number"
                    min="1"
                    max="365"
                    value={permissions.history_days_limit}
                    onChange={(e) =>
                      setPermissions({
                        ...permissions,
                        history_days_limit: parseInt(e.target.value) || 30,
                      })
                    }
                    className="w-32"
                  />
                  <p className="text-xs text-muted-foreground">
                    Número de dias anteriores que o usuário pode visualizar
                  </p>
                </div>
              )}
            </div>

            {/* Enviar Comandos */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enviar Comandos</Label>
                <p className="text-sm text-muted-foreground">
                  Permite enviar comandos remotos aos dispositivos (ex: bloqueio)
                </p>
              </div>
              <Switch
                checked={permissions.can_send_commands}
                onCheckedChange={(checked) =>
                  setPermissions({ ...permissions, can_send_commands: checked })
                }
              />
            </div>

            {/* Editar Dispositivos */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Editar Dispositivos</Label>
                <p className="text-sm text-muted-foreground">
                  Permite cadastrar, editar e excluir dispositivos GPS
                </p>
              </div>
              <Switch
                checked={permissions.can_edit_devices}
                onCheckedChange={(checked) =>
                  setPermissions({ ...permissions, can_edit_devices: checked })
                }
              />
            </div>

            {/* Ver Relatórios */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Ver Relatórios</Label>
                <p className="text-sm text-muted-foreground">
                  Permite gerar e visualizar relatórios de rastreamento
                </p>
              </div>
              <Switch
                checked={permissions.can_view_reports}
                onCheckedChange={(checked) =>
                  setPermissions({ ...permissions, can_view_reports: checked })
                }
              />
            </div>

            {/* Compartilhar Rastreamento */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Compartilhar Rastreamento</Label>
                <p className="text-sm text-muted-foreground">
                  Permite gerar links de compartilhamento público
                </p>
              </div>
              <Switch
                checked={permissions.can_share_tracking}
                onCheckedChange={(checked) =>
                  setPermissions({ ...permissions, can_share_tracking: checked })
                }
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={updatePermissions.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={updatePermissions.isPending}>
            {updatePermissions.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar Permissões'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
